import { React, useState, useCallback, useEffect } from 'react'
import { Card } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { colors } from 'globalStyles'
import SVGIcon from 'reusableComponents/SVGIcon'
import VerifyOTPModal from 'components/VerifyOTPModal'
import SuccessModal from 'components/SuccessModal'
import { phoneUpdateSendOTP } from 'api/shipping'
import { updateFullName, updateUserEmail, logoutUser } from 'api/accounts'
import ProfileView from 'components/MyProfile/ProfileView'
import PropTypes from 'prop-types'
import PopUp from 'reusableComponents/PopUp'
import { updateLoginEmail, getUserProfileInfo } from 'store/recipient/recipientSlice'
import { setHasSession } from 'store/shipping/shippingSlice'
import { ReactComponent as EmailSVG } from 'assets/svg/email.svg'
import { maskEmail, getDOMEncodeString } from 'utils/helperFunctions'
import EditForm from './EditForm'
import CancelPopUp from './CancelPopUp'

const pywBackendURL = process.env.REACT_APP_PYW_BACKEND_API

const StyledBaseCard = styled(Card)`
  display: flex;
  flex-direction: row !important;
  position: relative;
  padding: 8px 4px;
  border-radius: 8px 0px;
  min-height: 52px;
  width: 100%;
  border-radius: 8px 0px;
  .card-body {
    display: block;
  }
  .card-title {
    text-align: center;
    color: ${colors.dark_blue};
    font-weight: 600;
    font-size: 20px;
  }

  input#lastNameInput,
  input#firstNameInput,
  input#mobileNumberInput,
  input#emailAddressInput {
    width: 100%;
    margin-bottom: 20px;
    margin-top: 20px;
    border: 1px solid ${colors.secondary_grey};
  }

  .was-validated .form-control:valid,
  .form-control.is-valid {
    background-image: none;
  }

  .was-validated .form-control:valid:focus,
  .form-control.is-valid:focus {
    box-shadow: none;
  }

  button {
    width: 100%;
    flex: 1;
    &:not(:last-child) {
      margin-right: 10px;
    }
  }

  .requiredFieldHeader {
    text-align: left;
    font-size: 13px;
    margin-top: 30px;
    margin-bottom: 20px;
  }

  .last-char {
    content: attr(data-last-char);
    color: ${colors.secondary_red} !important;
  }
`
const StyledDiv = styled.div`
  font-size: 14px;
  line-height: 20px;
  margin-top: 25px;
  text-align: center;
`

const ResendLink = styled.a.attrs((props) => ({
  id: 'resend-link',
  rel: 'noreferrer',
  href: props.disabled ? undefined : '#',
}))`
  color: ${colors.primary_blue};
  cursor: pointer;
  text-decoration: underline;

  ${({ disabled }) =>
    disabled &&
    `
      pointer-events: none;
      color: gray;
      cursor: not-allowed;
      text-decoration: none;
  `}
`

const CloseLink = styled.a.attrs({
  id: 'close-link',
  href: '#',
  rel: 'noreferrer',
})`
  color: ${colors.primary_blue};
  cursor: pointer;
  text-decoration: underline;
`
const formatMobileNumber = (number) => {
  if (typeof number !== 'string') {
    console.error('Invalid input to formatMobileNumber:', number)
    return ''
  }
  const cleaned = number.replace(/[^\d]/g, '')
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/)
  return match ? `${match[1]} ${match[2]} ${match[3]} ${match[4]}` : number
}

const MyProfile = ({ userProfileDetails }) => {
  const prn = pywBackendURL
  const { t, i18n } = useTranslation()
  const { lang } = useParams()
  const { language } = i18n
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [profileData, setProfileData] = useState({
    firstName: userProfileDetails.firstName,
    lastName: userProfileDetails.lastName,
    mobileNumber: formatMobileNumber(userProfileDetails?.mobileNumber),
    emailAddress: userProfileDetails?.emailAddress,
  })

  const [editMode, setEditMode] = useState(null) // fullName, mobileNumber, emailAddress
  const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false)
  const [sendAgainDisabled, setSendAgainDisabled] = useState(false)
  const [showVerificationPopUp, setShowVerificationPopUp] = useState(false)
  const [updatePhoneSuccess, setUpdatePhoneSuccess] = useState(false)
  const [phoneUpdated, setPhoneUpdated] = useState(false)
  const [nameUpdated, setNameUpdated] = useState(false)
  const [showPhoneSuccessModal, setShowPhoneSuccessModal] = useState(false)
  const [fetchUpdatedUserDetails, setFetchUpdatedUserDetails] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showCancelPopUp, setShowCancelPopUp] = useState(false)
  const [popUpMsg, setPopUpMsg] = useState('')
  const resetProfileData = useCallback(() => {
    setProfileData({
      firstName: userProfileDetails.firstName,
      lastName: userProfileDetails.lastName,
      mobileNumber: formatMobileNumber(userProfileDetails.mobileNumber),
      emailAddress: userProfileDetails.emailAddress,
    })
    setEditMode(null)
  }, [userProfileDetails])

  useEffect(() => {
    if (sendAgainDisabled) {
      const zeroAttemptsTimeout = setTimeout(() => {
        setSendAgainDisabled(false)
      }, 1000 * 30)

      return () => {
        clearTimeout(zeroAttemptsTimeout)
      }
    }
    if (updatePhoneSuccess) {
      setShowPhoneSuccessModal(true)
      setFetchUpdatedUserDetails(true)
      setTimeout(() => {
        setShowPhoneSuccessModal(false)
        setPhoneUpdated(true)
      }, 3000)
    }

    if (fetchUpdatedUserDetails) dispatch(getUserProfileInfo(prn))

    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }

    return null
  }, [
    lang,
    i18n,
    language,
    sendAgainDisabled,
    updatePhoneSuccess,
    fetchUpdatedUserDetails,
    dispatch,
    prn,
  ])

  const handleEditClick = (field) => setEditMode(field)

  const handleEmailCloseClick = () => {
    setShowEmailVerifyModal(false)
    resetProfileData()
    logoutUser()
    dispatch(updateLoginEmail(true))
    dispatch(setHasSession(false))
    navigate(`/rs/tr/${lang}/login`, {
      replace: true,
    })
  }

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({ ...prev, [field]: value }))
  }

  const onClickResendEmail = async () => {
    setSendAgainDisabled(true)
    // eslint-disable-next-line no-console
    updateUserEmail(profileData.emailAddress)
  }

  const sendPhoneNumberUpdate = async (phoneNumber) => {
    try {
      const formattedNumber = phoneNumber.replace(/\s/g, '')
      await phoneUpdateSendOTP(formattedNumber, lang)
    } catch (error) {
      console.error('phoneUpdateSendOTP error')
    } finally {
      setShowVerificationPopUp(true)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    try {
      setIsLoading(true)
      if (editMode === 'fullName') {
        // Check if any changes were made
        const firstNameChanged =
          profileData.firstName.toLowerCase() !== userProfileDetails.firstName.toLowerCase()
        const lastNameChanged =
          profileData.lastName.toLowerCase() !== userProfileDetails.lastName.toLowerCase()

        if (!firstNameChanged && !lastNameChanged) {
          setEditMode(null)
          return
        }

        await updateFullName(profileData.firstName, profileData.lastName)
        setEditMode(null)
        setFetchUpdatedUserDetails(true)

        let message
        if (firstNameChanged && lastNameChanged) {
          message = t('my_profile.fullname_update_success_msg')
        } else if (firstNameChanged) {
          message = t('my_profile.firstname_update_success_msg')
        } else if (lastNameChanged) {
          message = t('my_profile.lastname_update_success_msg')
        }

        setSuccessMessage(message)
        setShowModal(true)
        setTimeout(() => {
          setShowModal(false)
          setNameUpdated(true)
        }, 3000)
      } else if (editMode === 'emailAddress') {
        // Check if email was changed
        if (
          profileData.emailAddress.toLowerCase() === userProfileDetails.emailAddress.toLowerCase()
        ) {
          setEditMode(null)
          return
        }

        setShowEmailVerifyModal(true)
        updateUserEmail(profileData.emailAddress)
        setEditMode(null)
      } else if (editMode === 'mobileNumber') {
        // Check if phone number was changed
        if (
          formatMobileNumber(profileData.mobileNumber) ===
          formatMobileNumber(userProfileDetails.mobileNumber)
        ) {
          setEditMode(null)
          return
        }

        await sendPhoneNumberUpdate(profileData.mobileNumber)
        setEditMode(null)
      }
    } catch (err) {
      setEditMode(null)
      console.error('Failed to update user profile.')
    } finally {
      setIsLoading(false)
    }
  }
  const cancelModal = () => {
    setShowModal(false)
  }

  const onClickYes = async (event) => {
    event.preventDefault()
    setShowCancelPopUp(false)
    resetProfileData()
  }

  const cancelPhoneSuccessModal = () => {
    setShowPhoneSuccessModal(false)
  }

  const onClickNo = async (event) => {
    event.preventDefault()
    setShowCancelPopUp(false)
  }

  const onClickCancel = async (event) => {
    event.preventDefault()
    if (editMode === 'emailAddress') {
      setPopUpMsg(t('my_profile.email_change_confirmation'))
    } else if (editMode === 'mobileNumber') {
      setPopUpMsg(t('my_profile.mobile_num_change_confirmation'))
    } else if (editMode === 'fullName') {
      setPopUpMsg(t('my_profile.name_change_confirmation'))
    } else {
      resetProfileData()
      return
    }
    setShowCancelPopUp(true)
  }

  const handleCheckMarkComplete = () => {
    if (phoneUpdated) {
      setPhoneUpdated(false)
      setUpdatePhoneSuccess(false)
    } else if (nameUpdated) {
      setNameUpdated(false)
    }
  }

  return (
    <>
      <StyledBaseCard>
        <Card.Body>
          <Card.Title>{t(`my_profile.profile_details`)}</Card.Title>
          {!editMode ? (
            <ProfileView
              profileData={profileData}
              onEditClick={handleEditClick}
              t={t}
              phoneUpdated={phoneUpdated}
              nameUpdated={nameUpdated}
              checkMarkCompleteStatus={handleCheckMarkComplete}
            />
          ) : (
            <EditForm
              editMode={editMode}
              profileData={profileData}
              onInputChange={handleInputChange}
              onCancel={onClickCancel}
              onSubmit={handleSubmit}
              t={t}
              isLoading={isLoading}
            />
          )}
        </Card.Body>
      </StyledBaseCard>
      <StyledDiv>{t(`my_profile.update_number_msg`)}</StyledDiv>
      <VerifyOTPModal
        show={showVerificationPopUp}
        onHide={setShowVerificationPopUp}
        phoneNumber={profileData.mobileNumber}
        isUpdatePhoneSuccess={setUpdatePhoneSuccess}
        resetProfileData={resetProfileData}
        showModalFor="updatePhone"
      />
      <SuccessModal
        show={showPhoneSuccessModal}
        onHide={cancelPhoneSuccessModal}
        message={t(`my_profile.phone_update_success_msg`)}
      />
      <PopUp
        title={t('verificationPopup.verification_title')}
        show={showEmailVerifyModal}
        size="md"
        icon={getDOMEncodeString(<SVGIcon Icon={EmailSVG} color={colors.primary_black} />)}
        iconWidth="45px"
        onHide={setShowEmailVerifyModal}
      >
        <section>
          <div className="row">
            <div className="col" style={{ textAlign: 'center', marginTop: '24px' }}>
              <Trans
                t={t}
                i18nKey="recipientInfo.verify_identity_message"
                values={{ email: maskEmail(profileData?.emailAddress) }}
                components={[<span style={{ color: colors.primary_blue }} />]}
              />
            </div>
          </div>
          <div className="row">
            <div className="col" style={{ textAlign: 'center', marginTop: '24px' }}>
              <p>{t('recipientInfo.verify_expires')}</p>
            </div>
          </div>
          <div className="row">
            <div className="col" style={{ textAlign: 'center', marginTop: '24px' }}>
              <Trans
                t={t}
                i18nKey="login.no_email_received"
                components={[
                  <ResendLink onClick={onClickResendEmail} disabled={sendAgainDisabled} />,
                ]}
              />
            </div>
          </div>
          <div className="row">
            <div className="col" style={{ textAlign: 'center', marginTop: '24px' }}>
              <Trans
                t={t}
                i18nKey="close_link"
                components={[<CloseLink onClick={handleEmailCloseClick} />]}
              />
            </div>
          </div>
        </section>
      </PopUp>
      <SuccessModal show={showModal} onHide={cancelModal} message={successMessage} />
      <CancelPopUp
        popUpText={popUpMsg}
        show={showCancelPopUp}
        onClickYes={onClickYes}
        onClickNo={onClickNo}
      />
    </>
  )
}

const userProfileDetails = PropTypes.shape({
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  mobileNumber: PropTypes.string.isRequired,
  emailAddress: PropTypes.string.isRequired,
})

MyProfile.propTypes = {
  userProfileDetails: userProfileDetails.isRequired,
}

export default MyProfile
